import { storageService } from '@/services/storage'

import type { SearchHistoryRecord } from '../types'

const STORAGE_KEY = 'masterhub.search.history'
const FAVORITES_KEY = 'masterhub.search.favorites'
const MAX_HISTORY = 50

/**
 * SearchRepository — persistence for search history & favorites.
 * Browser uses StorageService (SPA-safe). Shape mirrors Prisma SearchHistory
 * for a future API swap without changing consumers.
 */
export class SearchRepository {
  listHistory(): SearchHistoryRecord[] {
    return storageService
      .getJSON<SearchHistoryRecord[]>(STORAGE_KEY, [])
      .slice()
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }

  addHistory(input: {
    searchTerm: string
    totalResults: number
    executionTime: number
  }): SearchHistoryRecord {
    const now = new Date().toISOString()
    const record: SearchHistoryRecord = {
      id: crypto.randomUUID(),
      searchTerm: input.searchTerm,
      date: now,
      totalResults: input.totalResults,
      executionTime: input.executionTime,
      createdAt: now,
      updatedAt: now,
    }

    const next = [record, ...this.listHistory()].slice(0, MAX_HISTORY)
    storageService.setJSON(STORAGE_KEY, next)
    return record
  }

  clearHistory(): void {
    storageService.setJSON(STORAGE_KEY, [])
  }

  listFavorites(): string[] {
    return storageService.getJSON<string[]>(FAVORITES_KEY, [])
  }

  toggleFavorite(term: string): string[] {
    const normalized = term.trim()
    if (!normalized) return this.listFavorites()

    const current = this.listFavorites()
    const exists = current.some((t) => t.toLowerCase() === normalized.toLowerCase())
    const next = exists
      ? current.filter((t) => t.toLowerCase() !== normalized.toLowerCase())
      : [normalized, ...current].slice(0, 30)

    storageService.setJSON(FAVORITES_KEY, next)
    return next
  }
}

export const searchRepository = new SearchRepository()
